import { LOG_EVENT } from 'common/redux/modules/logging'

import types from 'tickets/redux/modules/types'
import {
  getSelectedOutboundOriginDestination,
  getSelectedInboundOriginDestination,
  getSelectedOutboundIsDirectBooking,
  getSelectedInboundIsDirectBooking,
  getSelectedOutboundDepartureDateTime,
  getSelectedInboundDepartureDateTime,
  getOutboundCartBook,
  getInboundCartBook,
} from 'tickets/redux/modules/searchResponses/selectors'

import {
  logGaEvent,
  logGaNonInteractionEvent,
  logGaAddToCartEvent,
  logGaRemoveFromCartEvent,
  formatGaProductName,
  logBackendEvent,
  logFunnelLanding,
  logFunnelExit,
} from 'common/utils/analytics'

import { isStringFormat } from 'common/utils/constants'

import {
  getPage,
} from 'tickets/redux/modules/modesInfo/selectors'

const TICKET_CATEGORY = 'Tickets'

export default store => next => action => {
  // Before state update
  switch (action.type) {
    case LOG_EVENT:
      logGaEvent(action.category, action.action, action.label, action.value)
      break
    default:
      handleTicketEvent(action, store)
      handleLocationEvent(action)
      handlePassengersEvent(action)
      break
  }

  // Update state
  const result = next(action)

  // After state update
  handleTicketEventComplete(action, store)

  return result
}

const handleTicketEvent = (action, store) => {
  const state = store.getState()

  switch (action.type) {
    case types.LOG_PERFORM_SEARCH:
      logGaNonInteractionEvent(TICKET_CATEGORY, 'Milestone:PerformSearch')
      break
    case types.PAGE_INCREASE:
      const pageIndex = getPage(state)
      logGaEvent(TICKET_CATEGORY, 'Clicked:LoadMore', pageIndex + 1)
      logBackendEvent('Clicked:LoadMore')
      break
    case types.TICKET_INBOUND_REMOVED: {
      const removedInbound = inboundAnalyticsData(state)
      const providerName = providerNameFromAnalyticsData(removedInbound)
      if (getSelectedInboundIsDirectBooking(state)) {
        const product = gaECommerceProduct(
          getSelectedInboundOriginDestination(state),
          providerName
        )
        logGaRemoveFromCartEvent(product, TICKET_CATEGORY, 'InboundRemoved', providerName)
      } else {
        logGaEvent(TICKET_CATEGORY, 'InboundRemoved', providerName)
      }
      logBackendEvent('InboundRemoved', removedInbound)
      break
    }
    case types.TICKET_OUTBOUND_REMOVED: {
      const removedOutbound = outboundAnalyticsData(state)
      const providerName = providerNameFromAnalyticsData(removedOutbound)
      if (getSelectedOutboundIsDirectBooking(state)) {
        const product = gaECommerceProduct(
          getSelectedOutboundOriginDestination(state),
          providerName
        )
        logGaRemoveFromCartEvent(product, TICKET_CATEGORY, 'OutboundRemoved', providerName)
      } else {
        logGaEvent(TICKET_CATEGORY, 'OutboundRemoved', providerName)
      }
      logBackendEvent('OutboundRemoved', removedOutbound)
      break
    }
    case types.TICKET_CLEAR_RESULTS:
      logGaEvent(TICKET_CATEGORY, 'ClearedResults')
      break
    case types.MODE_CHANGED:
      logGaEvent(TICKET_CATEGORY, 'TabChanged', `${action.payload.prevMode} -> ${action.payload.mode}`)
      break
    case types.MODE_INITIALIZED:
      logGaNonInteractionEvent(TICKET_CATEGORY, 'ModeInit', action.payload)
    case types.OUTBOUND_FARE_CLASS_SELECTED:
      logGaEvent(TICKET_CATEGORY, 'FareTypeChanged', 'Outbound')
      break
    case types.INBOUND_FARE_CLASS_SELECTED:
      logGaEvent(TICKET_CATEGORY, 'FareTypeChanged', 'Inbound')
      break
    default:
  }
}

const handleTicketEventComplete = (action, store) => {
  const state = store.getState()
  switch (action.type) {
    case types.TICKET_INBOUND_SELECTED: {
      const selectedInbound = inboundAnalyticsData(state)
      const providerName = providerNameFromAnalyticsData(selectedInbound)
      if (getSelectedInboundIsDirectBooking(state)) {
        const product = gaECommerceProduct(
          getSelectedInboundOriginDestination(state),
          providerName
        )
        logGaAddToCartEvent(product, TICKET_CATEGORY, 'Milestone:InboundSelected', providerName)
      } else {
        logGaEvent(TICKET_CATEGORY, 'Milestone:InboundSelected', providerName)
      }
      logBackendEvent('InboundSelected', inboundAnalyticsData)
      break
    }
    case types.TICKET_OUTBOUND_SELECTED: {
      const selectedOutbound = outboundAnalyticsData(state)
      const providerName = providerNameFromAnalyticsData(selectedOutbound)
      if (getSelectedOutboundIsDirectBooking(state)) {
        const product = gaECommerceProduct(
          getSelectedOutboundOriginDestination(state),
          providerName
        )
        logGaAddToCartEvent(product, TICKET_CATEGORY, 'Milestone:OutboundSelected', providerName)
      } else {
        logGaEvent(TICKET_CATEGORY, 'Milestone:OutboundSelected', providerName)
      }
      logBackendEvent('OutboundSelected', selectedOutbound)
      break
    }
    case types.BOOK_NOW_INITIATED: {
      let eventData = outboundAnalyticsData(state)
      const inboundData = inboundAnalyticsData(state)
      if (inboundData != null) {
        eventData = {
          ...eventData,
          ...inboundData,
        }
      }
      const providerName = providerNameFromAnalyticsData(eventData)
      logGaEvent(TICKET_CATEGORY, 'Milestone:BookNowInitiated', providerName)
      logBackendEvent('BookNowInitiated', eventData)
      const checkoutType = isDirectBookingFromAnalyticsData(eventData) ? '' : 'Offsite'
      logFunnelExit(TICKET_CATEGORY, `${checkoutType}Checkout`, [providerName])
      break
    }
    default:
  }
}

const providerNameFromAnalyticsData = (bookingData) => {
  if (bookingData && bookingData.outboundBookingInfo) {
    return bookingData.outboundBookingInfo.providerName
  } else if (bookingData && bookingData.inboundCartBook) {
    return bookingData.inboundCartBook.providerName
  }
  return 'noneselected'
}

const isDirectBookingFromAnalyticsData = (bookingData) => {
  if (bookingData && bookingData.outboundBookingInfo) {
    return bookingData.outboundBookingInfo.isDirectBooking
  } else if (bookingData && bookingData.inboundCartBook) {
    return bookingData.inboundCartBook.isDirectBooking
  }
  return false
}

const outboundAnalyticsData = (state) => {
  const outboundOriginDestination = getSelectedOutboundOriginDestination(state)
  if (outboundOriginDestination != null) {
    const outboundDateTimeMoment = getSelectedOutboundDepartureDateTime(state).toMomentUTC()
    const outboundDateTime = outboundDateTimeMoment.format(isStringFormat)
    return {
      outboundOriginDestination,
      outboundDateTime,
      outboundBookingInfo: parseCartBookForAnalytics(getOutboundCartBook(state), true),
    }
  }
  return null
}

const inboundAnalyticsData = (state) => {
  const inboundOriginDestination = getSelectedInboundOriginDestination(state)
  if (inboundOriginDestination != null) {
    const inboundDateTimeMoment = getSelectedInboundDepartureDateTime(state).toMomentUTC()
    const inboundDateTime = inboundDateTimeMoment.format(isStringFormat)
    return {
      inboundOriginDestination,
      inboundDateTime,
      inboundCartBook: parseCartBookForAnalytics(getInboundCartBook(state), false),
    }
  }
  return null
}

const parseCartBookForAnalytics = (cartBook, isOutbound) => {
  let providerName = ''
  try {
    providerName = (isOutbound)
    ? cartBook.bookingData.outbound.providerBookingData.providerName
    : cartBook.bookingData.inbound.providerBookingData.providerName
  } catch (error) {
    providerName = 'unknown'
  }

  return ({
    agent: cartBook.agent,
    modeId: cartBook.modeId,
    price: cartBook.price,
    type: cartBook.type,
    providerName,
    currencyCode: cartBook.currencyCode,
    isDirectBooking: cartBook.isDirectBooking,
  })
}

const handleLocationEvent = action => {
  switch (action.type) {
    default:
  }
}

const handlePassengersEvent = action => {
  switch (action.type) {
    case types.PAX_UPDATE:
      logGaNonInteractionEvent(TICKET_CATEGORY, 'CancelUpdatingPassengers')
      break
    case types.PASSENGER_ADD:
      logGaEvent(TICKET_CATEGORY, 'PassengerAdd', action.payload.type)
      break
    case types.PASSENGER_REMOVE:
      logGaEvent(TICKET_CATEGORY, 'PassengerRemove', action.payload.type)
      break

    default:
  }
}

const gaECommerceProduct = (ticket, providerName) => {
  return {
    name: formatGaProductName(
      ticket.departingLocation,
      ticket.arrivalLocation,
    ),
    brand: providerName,
  }
}
